import { nanoid } from 'nanoid';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import { dropAt } from '../../../../utils';
import Button from '../../../generic/Button/Button';
import NavigationButton from '../../../generic/Button/NavigationButton';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../../generic/Icon';
import { DOCS_URL } from '../../../generic/Link';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import type { GemSource } from '../../../../../typings/auth';

import Source from './Source';

const RUBYGEMS_DOCS_LINK = `${DOCS_URL}/ruby`;

const GemSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isLoading,
    isSaveButtonDisabled,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'gem',
  );

  const sources = settings?.sources || [];

  const onAddSource = () =>
    onUpdateSettings({
      sources: [
        ...sources,
        { _id: nanoid() },
      ],
    });

  const onRemoveSource = (index: number) =>
    onUpdateSettings({
      sources: dropAt(sources, index),
    });

  const onUpdateSource = <K extends keyof GemSource>(
    index: number,
    member: K,
    value: GemSource[K],
  ) =>
      onUpdateSettings({
        sources: sources.map((source, i): GemSource => {
          if (i !== index) {
            return source;
          }

          return {
            ...source,
            [member]: value,
            hasPassword: member === 'password' ? false : source.hasPassword,
          };
        }),
      });

  const isFreeOrg = isOrgInFreeTier();

  return (
    <PaidFeatureGate isRestricted={isFreeOrg} hidePill>
      <Header heading="RubyGems Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="RubyGems Sources">
              <Section.Paragraph>
                Configure your Rubygems authenticated sources.
              </Section.Paragraph>
              <NavigationButton
                to={RUBYGEMS_DOCS_LINK}
                size="medium"
                type="secondary"
                openInNewTab
              >
                View documentation
              </NavigationButton>
            </Section.Left>
            <Section.Right>
              {sources.map((source, index) =>
                <Source
                  source={source}
                  onRemoveSource={() => onRemoveSource(index)}
                  onUpdateSource={
                    <K extends keyof GemSource>(
                      member: K,
                      value: GemSource[K],
                    ) => {
                      onUpdateSource(index, member, value);
                    }
                  }
                />,
              )}
              <Button
                type="secondary"
                size="medium"
                onClick={onAddSource}
              >
                <IconText icon={icons.plus} text="Add repository" />
              </Button>
            </Section.Right>
          </Section>
          )}
    </PaidFeatureGate>
  );
};

export default GemSettings;
